import { GridColDef, GridRowId } from "@mui/x-data-grid"
import { ChangeEvent, useCallback, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { TFunction } from "i18next"
import CircularProgress from "@mui/material/CircularProgress"

import useGetUtils from "@/app/settings/club/equipments/delivery/hooks/useUtils"
import {
  useDeliveryInfoQuery,
  useDeliveryListQuery,
  useReceiveDeliveryMutation,
} from "@/hooks/api/useDeliveryQuery"
import Table from "@/components/table/Table"
import Search from "@/components/form/search/Search"
import Pagination from "@/components/table/Pagination"
import { DeliverySummary } from "@/types/delivery"
import { DeliveryTableCol, Device } from "@/types/device"
import { createAccountStore } from "@/stores/account"
import { PAGE_SIZE } from "@/constants/pagination"
import BasicButton from "@/components/button/BasicButton"
import { BaseCheckbox } from "@/components/form/checkbox/Checkbox"
import { createGlobalStore } from "@/stores/global"

import "./deliveryPage.scss"

export default function DeliveryPage() {
  const { t } = useTranslation()
  const [expandedRowId, setExpandedRowId] = useState<Set<GridRowId>>(new Set())
  const [search, setSearch] = useState("")

  const { parseItem, parseStatus, parseType } = useGetUtils()
  const { data: deliveryList } = useDeliveryListQuery()

  const columns: GridColDef<DeliveryTableCol>[] = [
    {
      field: "idfTransfer",
      renderHeader: () => t("no"),
      maxWidth: 60,
      flex: 1,
    },
    {
      field: "type",
      renderHeader: () => t("type"),
      flex: 1,
      valueGetter: (value) => parseType(value as "D" | "R", t),
    },
    {
      field: "dateDeparted",
      renderHeader: () => t("date"),
      flex: 1,
    },
    {
      field: "item",
      valueGetter: (_, item) => parseItem(item, t),
      renderHeader: () => t("item"),
      flex: 1,
    },
    {
      field: "status",
      renderHeader: () => t("deliveryStatus"),
      flex: 1,
      cellClassName: (params) => {
        return params.row.status === "D" ? "shipped" : ""
      },
      valueGetter: (value) => parseStatus(value, t),
    },
  ]

  const formattedDeliveryList = useMemo(() => {
    return (
      deliveryList?.map((delivery) => ({
        ...delivery,
        id: delivery.idfTransfer,
      })) ?? []
    )
  }, [deliveryList])

  const rows = useMemo(() => {
    if (search.length <= 1) {
      return formattedDeliveryList
    }

    return formattedDeliveryList.filter((row) => {
      return Object.entries(row).some(([key, value]) => {
        let parsedValue = value.toString().toLowerCase()

        switch (key) {
          case "type":
            parsedValue = parseType(value as "D" | "R", t).toLowerCase()
            break
          case "status":
            parsedValue = parseStatus(value as "C" | "D", t).toLowerCase()
            break
          case "quantityCell":
          case "quantityDock":
          case "quantityGear":
          case "quantityAPBox":
            parsedValue = parseItem(row, t).toLowerCase()
            break
        }

        return parsedValue.includes(search.toLowerCase())
      })
    })
  }, [formattedDeliveryList, search])

  const handleDetailPanelExpandedRowIdsChange = useCallback((newIds: Set<GridRowId>) => {
    if (newIds.size > 1) {
      const newSet = new Set<GridRowId>()
      const newIdsArray = Array.from(newIds)
      newSet.add(newIdsArray[newIdsArray.length - 1])
      setExpandedRowId(newSet)
    } else {
      setExpandedRowId(newIds)
    }
  }, [])

  return (
    <div className="DeliveryPageCont">
      <h2>{t("delivery")}</h2>
      <Search onChange={(value) => setSearch(value)} />

      <Table
        columns={columns}
        rows={rows}
        getRowId={(row) => row.idfTransfer}
        initialState={{
          sorting: { sortModel: [{ field: "idfTransfer", sort: "desc" }] },
          pagination: {
            paginationModel: {
              pageSize: PAGE_SIZE,
            },
          },
        }}
        renderDetails={(row) => <DeliveryDetail {...row} />}
        detailPanelExpandedRowIds={expandedRowId}
        onDetailPanelExpandedRowIdsChange={handleDetailPanelExpandedRowIdsChange}
        pagination
        slots={{
          pagination: Pagination,
        }}
        disableColumnResize
        disableColumnSorting
        disableVirtualization
      />
    </div>
  )
}

function DeliveryDetail(prop: Record<string, unknown>, isOpen?: boolean) {
  const delivery = prop as DeliverySummary

  const { t } = useTranslation()
  const { teamInfo } = createAccountStore((state) => ({ teamInfo: state.teamProfileInfo }))
  const setSnackBar = createGlobalStore((state) => state.setSnackBar)

  const { data: detail, isLoading } = useDeliveryInfoQuery(delivery.idfTransfer, isOpen)
  const { mutate: receiveDelivery } = useReceiveDeliveryMutation(delivery.idfTransfer)

  const { parseStatus } = useGetUtils()

  const isShipped = detail?.status === "D"

  const [checkedDeviceTypes, setCheckedDeviceTypes] = useState<Set<string>>(new Set())

  const handleCheckChange = (deviceType: string, checked: boolean) => {
    setCheckedDeviceTypes((prev) => {
      const next = new Set(prev)
      if (checked) {
        next.add(deviceType)
      } else {
        next.delete(deviceType)
      }

      return next
    })
  }

  const handleClick = () => {
    receiveDelivery(
      { idfTransfer: delivery.idfTransfer, deviceTypes: Array.from(checkedDeviceTypes) },
      {
        onSuccess: () => {
          setSnackBar({
            message: t("messages.successUpdate"),
            severity: "success",
            open: true,
          })
          setCheckedDeviceTypes(new Set())
        },
      },
    )
  }

  return (
    <div className="DeliveryDetailCont">
      <div className="delivery-detail-header">
        <div>
          <p className="header-label">{t("deliveryId")}</p>
          <p className="header-value">{detail?.idfTransfer}</p>
        </div>
        <div>
          <p className="header-label">{t("destination")}</p>
          <p className="header-value">{teamInfo?.name}</p>
        </div>
        <div>
          <p className="header-label">{t("deliveryStatus")}</p>
          <p className="header-value">{parseStatus(detail?.status as "C" | "D", t)}</p>
        </div>
      </div>
      <div>
        {!isLoading &&
          !!detail?.dockList.length &&
          DeviceDeliverySection({
            devices: detail?.dockList || [],
            deviceModel: "docks",
            deviceType: "dock",
            t,
            showCheckbox: isShipped,
            checked: checkedDeviceTypes.has("dock"),
            onCheckChange: handleCheckChange,
          })}
        {!isLoading &&
          !!detail?.apboxList.length &&
          DeviceDeliverySection({
            devices: detail?.apboxList || [],
            deviceModel: "liveHubs",
            deviceType: "apbox",
            t,
            showCheckbox: isShipped,
            checked: checkedDeviceTypes.has("apbox"),
            onCheckChange: handleCheckChange,
          })}
        {!isLoading &&
          !!detail?.cellList.length &&
          DeviceDeliverySection({
            devices: detail?.cellList || [],
            deviceModel: "cells",
            deviceType: "cell",
            t,
            showCheckbox: isShipped,
            checked: checkedDeviceTypes.has("cell"),
            onCheckChange: handleCheckChange,
          })}
      </div>
      {isShipped && (
        <BasicButton
          type="primary-outlined"
          size="sm"
          style={{ height: "32px", alignSelf: "flex-end" }}
          onClick={handleClick}
          disabled={checkedDeviceTypes.size === 0}>
          {t("button.checkDelivery")}
        </BasicButton>
      )}
      {isLoading && (
        <div className="delivery-detail-loading">
          <CircularProgress />
        </div>
      )}
    </div>
  )
}

function DeviceDeliverySection({
  devices,
  deviceModel,
  deviceType,
  t,
  showCheckbox,
  checked,
  onCheckChange,
}: {
  devices: Device[]
  deviceModel: string
  deviceType: string
  t: TFunction<"", undefined>
  showCheckbox: boolean
  checked: boolean
  onCheckChange: (deviceType: string, checked: boolean) => void
}) {
  const models = [...new Set(devices.map(({ model, version }) => `${model}-${version}`))]

  return (
    <div className="delivery-detail-body">
      <div className="delivery-detail-body-header">
        <p className="model-header">
          <span className="model-type">{t(deviceModel)}</span>
          <span className="model-versions">{models.join(" / ")}</span>
        </p>
        <span>
          {showCheckbox && (
            <BaseCheckbox
              checked={checked}
              onChange={(_: ChangeEvent<HTMLInputElement>, val: boolean) =>
                onCheckChange(deviceType, val)
              }
            />
          )}
          {devices.length} {t("ea")}
        </span>
      </div>
      <div className="delivery-detail-body-content">
        {devices.map((device) => (
          <p className="device-serial" key={`${device.model}-${device.version}-${device.number}`}>
            {device.model}-{device.version}-{device.number.toString().padStart(5, "0")}
          </p>
        ))}
      </div>
    </div>
  )
}
